import { useQuery } from '@tanstack/react-query'

import { ICurrentProvider } from '@/app/providers/CurrentProvider'

import EnvironmentProvider, { useEnvironment } from '../../../providers/EnvironmentProvider'
import { IResultadoCircunscripcion } from '../circunscripciones/[circunscripcion]/components/CandidatesSection/CandidatesTable'
import { IResultado } from '../circunscripciones/[circunscripcion]/components/CandidatesSection/CandidatesTable/tableColumns'
import { IMesasEscrutadas } from '../components/CountedTables/IMesasEscrutadas'
import { IComparadorElecciones } from '../components/ListVotesPerElection/types'
import { IParticipacion } from '../components/ParticipationSection/ParticipationTable/types'
import { IComparadorRechazo } from '../consejeros/types/comparadorRechazo'
import { IGanador } from '../consejeros/types/ganadores'
import ganadoresCircunscripcionEmpty from './ganadoresCircunscripcionEmpty'
import { emptyGanadoresWithData } from './ganadoresWithDataMock'

export interface IBlancosNulosValidosPais {
  blancos: number
  nulos: number
  validos: number
}

export interface ILiveData {
  ganadores?: IGanador[]
  lastUpdate: Date
  ganadoresCircunscripcion?: IResultadoCircunscripcion[]
  mesasEscrutadas?: IMesasEscrutadas[]
  participacion?: IParticipacion
  comparadorRechazo?: IComparadorRechazo
  evolucionPorLista?: IComparadorElecciones
  resultadosCandidatos?: IResultado[]
  resultadosBlancosNulos?: IBlancosNulosValidosPais
}
interface ICurrentLive {
  candidatos: string // ICandidatos (Ver interfaces de Previa)
  ganadores: string // ➝ IGanadores
  participacion: string // ➝ IParticipacion
  mesas_escrutadas: string // ➝ IMesasEscrutadas
  comparador_rechazo: string // ➝ IComparadorRechazo
  ganadores_circunscripcion: string // ➝ IResultadosCircunscripcion
  evolucion_por_lista: string // ➝ IComparadorElecciones
  updated_at: string // 2023-05-04T19:54:58-04:00
  blancos_nulos_pais: string // IBlancosNulosPais
}

export interface ILiveDataProvider<T = unknown> extends ICurrentProvider<T> {
  liveData: ILiveData
}

export type ILiveDataEnv = ILiveDataProvider<ICurrentLive>

const LiveDataProvider = (props: object) => {
  const { currents, requests } = useEnvironment<ILiveDataEnv>()

  const liveCurrent = currents.live
  const date = liveCurrent?.updated_at
  const lastUpdate = liveCurrent && new Date(date)

  const queryParams = ({ enabled, url }: { url?: string; enabled?: boolean }) =>
    [[url], () => requests['2023_consejeros']({ method: 'GET', url }), { enabled }] as const

  const { data: ganadores } = useQuery<unknown, unknown, IGanador[]>(
    ...queryParams({
      enabled: Boolean(liveCurrent),
      url: liveCurrent?.ganadores,
    })
  )

  const { data: mesas_escrutadas } = useQuery<unknown, unknown, IMesasEscrutadas[]>(
    ...queryParams({
      enabled: Boolean(liveCurrent),
      url: liveCurrent?.mesas_escrutadas,
    })
  )

  const { data: participacion } = useQuery<unknown, unknown, IParticipacion>(
    ...queryParams({
      enabled: Boolean(liveCurrent),
      url: liveCurrent?.participacion,
    })
  )

  const { data: ganadores_circunscripcion } = useQuery<
    unknown,
    unknown,
    IResultadoCircunscripcion[]
  >(
    ...queryParams({
      enabled: Boolean(liveCurrent),
      url: liveCurrent?.ganadores_circunscripcion,
    })
  )

  const resultadosCandidatos: IResultado[] | undefined = (
    ganadores_circunscripcion || ganadoresCircunscripcionEmpty
  )?.reduce(
    (resultadosAcc, { resultados }) => [...resultadosAcc, ...resultados],
    [] as IResultado[]
  )

  const { data: comparador_rechazo } = useQuery<unknown, unknown, IComparadorRechazo>(
    ...queryParams({
      enabled: Boolean(liveCurrent),
      url: liveCurrent?.comparador_rechazo,
    })
  )

  const { data: evolucion_por_lista } = useQuery<unknown, unknown, IComparadorElecciones>(
    ...queryParams({
      enabled: Boolean(liveCurrent),
      url: liveCurrent?.evolucion_por_lista,
    })
  )

  const { data: blancosNulosValidosPais } = useQuery<unknown, unknown, IBlancosNulosValidosPais>(
    ...queryParams({
      enabled: Boolean(liveCurrent),
      url: liveCurrent?.blancos_nulos_pais,
    })
  )

  const liveData: ILiveData = {
    comparadorRechazo: comparador_rechazo, // comparador_rechazo, // No necesita mock incial
    evolucionPorLista: evolucion_por_lista, // evolucion_por_lista, // Parte con las listas de la elección actual 2023_c en 0
    ganadores: ganadores || emptyGanadoresWithData, // mockWinners,
    ganadoresCircunscripcion: ganadores_circunscripcion || ganadoresCircunscripcionEmpty, // ganadores_circunscripcion, // Inicial: que no venga
    lastUpdate: lastUpdate,
    mesasEscrutadas: mesas_escrutadas, //mockMesasEscrutadas, // Traiga uno con timestamp anterior a las 6 pm con todas en 0.
    participacion: participacion, // participacion, //  p_votan: 0 p_no_votan: 0 c_votan: 0 c_no_votan: 0
    resultadosBlancosNulos: blancosNulosValidosPais,
    resultadosCandidatos,
  }

  return (
    <EnvironmentProvider
      env={{ liveData }}
      {...props}
    />
  )
}

export default LiveDataProvider
